import React, { useState, useEffect } from 'react';
import { Input } from '@/src/shadComponents/ui/input';

interface CurrencyInputProps {
    value?: number; // Value in cents
    onChange: (valueInCents: number) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    error?: string;
}

export default function CurrencyInput({
    value = 0,
    onChange,
    placeholder = '$0.00',
    disabled = false,
    className = '',
    error,
}: CurrencyInputProps) {
    // Display value in dollars (formatted)
    const [displayValue, setDisplayValue] = useState<string>('');

    // Convert cents to dollar display format
    const centsToDisplay = (cents: number): string => {
        const dollars = cents / 100;
        return dollars.toFixed(2);
    };

    // Convert dollar display to cents
    const displayToCents = (display: string): number => {
        const cleanedValue = display.replace(/[^\d.]/g, '');
        const dollars = parseFloat(cleanedValue) || 0;
        return Math.round(dollars * 100);
    };

    // Initialize display value when component mounts or value prop changes
    useEffect(() => {
        setDisplayValue(centsToDisplay(value));
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        // Allow only numbers and one decimal point
        const cleanedValue = inputValue.replace(/[^\d.]/g, '');

        // Prevent multiple decimal points
        const parts = cleanedValue.split('.');
        let formattedValue = parts[0];
        if (parts.length > 1) {
            // Limit to 2 decimal places
            formattedValue += '.' + parts[1].substring(0, 2);
        }

        setDisplayValue(formattedValue);

        // Convert to cents and call onChange
        const cents = displayToCents(formattedValue);
        onChange(cents);
    };

    const handleBlur = () => {
        // Format the display value on blur to ensure proper formatting
        const cents = displayToCents(displayValue);
        setDisplayValue(centsToDisplay(cents));
    };

    return (
        <div className="relative">
            <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none">
                    $
                </span>
                <Input
                    type="text"
                    value={displayValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder={placeholder.replace('$', '')}
                    disabled={disabled}
                    className={`pl-7 ${className} ${
                        error ? 'border-red-500' : ''
                    }`}
                />
            </div>
            {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
        </div>
    );
}
